import { useNavigate } from "@tanstack/react-router";
import { SkeletonBox } from "@web/components/skeleton";
import { surfaceBase } from "@web/components/ui";
import { useEffect, useState } from "react";
import { useToast } from "@/contexts";
import { getCurrentOrg } from "@/features/organizations/api";

interface OrgData {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  role: string;
  memberCount?: number;
  competitionCount?: number;
}

export default function OrgDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const [org, setOrg] = useState<OrgData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data, error } = await getCurrentOrg();
      if (error) {
        toast.showAlert("Error", error.message, "error");
        navigate({ to: "/org/create", replace: true });
        return;
      }
      if (data) setOrg(data);
      setLoading(false);
    })();
  }, [toast, navigate]);

  if (loading) {
    return (
      <div className="flex flex-col gap-4 px-5 pt-3">
        <SkeletonBox width="60%" height={32} />
        <SkeletonBox width="100%" height={120} />
        <SkeletonBox width="100%" height={80} />
      </div>
    );
  }

  if (!org) return null;

  const isAdmin = org.role === "admin" || org.role === "owner";

  return (
    <div className="flex flex-col gap-4">
      <div className={`${surfaceBase} bg-card p-5`}>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full bg-aqua" />
          <span className="text-text-muted text-[0.625rem] font-bold uppercase tracking-[0.075em]">
            Organization
          </span>
        </div>
        <span className="text-text text-[1.5rem] font-black tracking-[-0.0187em]">{org.name}</span>
        <span className="text-text-dim text-[0.8125rem] font-bold block mt-1">
          /{org.slug} &middot; Joined {new Date(org.createdAt).toLocaleDateString()}
        </span>
      </div>

      <div className="flex flex-row gap-3">
        <div className={`flex-1 ${surfaceBase} bg-card p-4`}>
          <span className="text-text text-[1.75rem] font-black">{org.memberCount ?? "—"}</span>
          <span className="text-text-dim text-[0.75rem] font-bold block mt-1">Members</span>
        </div>
        <div className={`flex-1 ${surfaceBase} bg-card p-4`}>
          <span className="text-text text-[1.75rem] font-black">{org.competitionCount ?? "—"}</span>
          <span className="text-text-dim text-[0.75rem] font-bold block mt-1">Competitions</span>
        </div>
      </div>

      {isAdmin && (
        <button
          type="button"
          onClick={() => navigate({ to: "/org/members" })}
          className={`${surfaceBase} bg-card p-4 flex items-center justify-between cursor-pointer text-left w-full`}
        >
          <span className="text-text text-[0.9375rem] font-bold">Manage Members</span>
          <span className="text-text-dim text-lg">&rarr;</span>
        </button>
      )}
    </div>
  );
}
